/* KoreCare behaviour: video playback, mute toggles, rail arrows, empty review sections.
   One file, no dependencies. Everything degrades to static if JS fails. */
(() => {
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* --- video: plays while near the screen ------------------------------- */
  // Muted autoplay, started half a screen before a video scrolls in; off-screen videos pause.
  const vio = new IntersectionObserver(
    (entries) => {
      entries.forEach((e) => {
        const v = e.target;
        if (e.isIntersecting) {
          // Posters wait in data-kc-poster until a reel is near the screen, so
          // none of them compete with the page's first image.
          if (v.dataset.kcPoster && !v.poster) v.poster = v.dataset.kcPoster;
          // Reduced motion: no autoplay; the poster stays and a tap plays it.
          // play() rejects if the browser blocks autoplay (iPhone Low Power Mode).
          if (!reduced) v.play().catch(() => {});
        } else {
          v.pause();
        }
      });
    },
    { rootMargin: '50% 0px', threshold: 0 }
  );
  const watchVideos = (root) => root.querySelectorAll('video[data-kc-video]').forEach((v) => vio.observe(v));
  watchVideos(document);

  /* --- mute toggles ---------------------------------------------------- */
  const setMuted = (v, muted) => {
    v.muted = muted;
    const btn = (v.closest('[data-kc-videowrap]') || document).querySelector('[data-kc-mute]');
    if (!btn) return;
    btn.setAttribute('aria-pressed', String(!muted));
    btn.querySelector('[data-kc-mute-on]')?.toggleAttribute('hidden', muted);
    btn.querySelector('[data-kc-mute-off]')?.toggleAttribute('hidden', !muted);
  };
  document.addEventListener('click', (e) => {
    // A tap on a reel itself works like its speaker button.
    const btn =
      e.target.closest('[data-kc-mute]') ||
      (e.target.matches('.kc-vid__cell video') && e.target.parentElement.querySelector('[data-kc-mute]'));
    if (!btn) return;
    const scope = btn.closest('[data-kc-videowrap]') || document;
    const v = scope.querySelector('video[data-kc-video]');
    if (!v) return;
    // One voice at a time.
    if (v.muted) document.querySelectorAll('video[data-kc-video]').forEach((o) => o !== v && !o.muted && setMuted(o, true));
    setMuted(v, !v.muted);
    if (v.paused) v.play().catch(() => {});
  });

  /* --- collapse a review section Judge.me never filled ----------------- */
  // Not hidden up front: Judge.me will not paint into a display:none container.
  // Collapse only if nothing arrived after 6 s (it replaces the node, so re-query).
  document.querySelectorAll('[data-kc-reviews]').forEach((section) => {
    if (!section.querySelector('.jdgm-widget')) return;
    setTimeout(() => {
      const w = section.querySelector('.jdgm-widget');
      if (!w || (!w.children.length && !w.textContent.trim())) section.hidden = true;
    }, 6000);
  });

  /* --- carousel: arrow keys step one cell, arrow buttons most of a view - */
  const initRail = (rail) => {
    const step = (dir, page) => {
      const cell = rail.querySelector('.kc-vid__cell');
      if (!cell) return false;
      const w = cell.offsetWidth + 16;
      rail.scrollBy({ left: (page ? Math.max(w, rail.clientWidth - w) : w) * dir, behavior: reduced ? 'auto' : 'smooth' });
      return true;
    };
    rail.addEventListener('keydown', (e) => {
      if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
      if (step(e.key === 'ArrowRight' ? 1 : -1)) e.preventDefault();
    });
    // Built here so a no-JS page has no dead buttons; hidden at the edge they point past (classes on the box).
    const box = rail.parentElement;
    ['prev', 'next'].forEach((dir) => {
      const el = document.createElement('button');
      el.type = 'button';
      el.className = `kc-vid__arrow kc-vid__arrow--${dir}`;
      el.setAttribute('aria-label', dir === 'next' ? 'More videos' : 'Previous videos');
      el.innerHTML =
        '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="square" aria-hidden="true"><path d="' +
        (dir === 'next' ? 'M9 5l7 7-7 7' : 'M15 5l-7 7 7 7') +
        '"/></svg>';
      el.addEventListener('click', () => step(dir === 'next' ? 1 : -1, true));
      box.append(el);
    });
    const edges = () => {
      const max = rail.scrollWidth - rail.clientWidth;
      box.classList.toggle('at-start', rail.scrollLeft <= 2);
      box.classList.toggle('at-end', rail.scrollLeft >= max - 2);
    };
    edges();
    rail.addEventListener('scroll', edges, { passive: true });
    addEventListener('resize', edges);
  };
  document.querySelectorAll('[data-kc-rail]').forEach(initRail);

  /* --- theme editor: a re-rendered section arrives as new elements ----- */
  document.addEventListener('shopify:section:load', (e) => {
    watchVideos(e.target);
    e.target.querySelectorAll('[data-kc-rail]').forEach(initRail);
  });
})();
